import convert from "convert-units";
import map from "lodash/map";
import range from "lodash/range";
import round from "lodash/round";

import { PARAMETER_OPTIONS } from "../../../../../../constants";
import { mapParameter } from "../../../../../../parts/other-exercise/CustomParametersSets";
import PRBadge from "../../../../../../parts/PRBadge";
import StatusIcon from "../../../../../../parts/StatusIcon";
import {
  expandSetOutcomes,
  getVelocityValue,
} from "../../../../../../utils/actuals";
import convertToRiR from "../../../../../../utils/converters/convert-intensity";
import { getLoadDrop } from "../../../../../../utils/load-drop";
import { repsValue } from "../../../../../../utils/reps-range";

const { RPE, RPEAMRAP, LOAD, REPS, SETS } = PARAMETER_OPTIONS;

const TableForAssistant = ({
  idx,
  exercise,
  editDayMode,
  editable,
  hasActuals = false,
  hasPR = false,
  prByRound = {},
  setNumberOffset = 0,
  set,
  handler,
  isTemporal,
  addTempSet,
  removeSet,
  intensityMeasure,
  isRiR,
  units,
  withWeight,
  fromMetric,
  hasLoadDrop,
}) => {
  // `source` is the round's outcome when the athlete logged one, otherwise the
  // prescribed set — same "actual if logged, else prescribed" rule the
  // read-only week view uses (WeekDayExercise).
  const renderParameter = (_param, source = set) => {
    const param = _param.toLowerCase();
    if (editDayMode && editable) {
      if ([RPE, RPEAMRAP, "rpe/amrap"].includes(param)) {
        return (
          <select
            id={`rpe-${exercise._id}-${idx}`}
            className="select-input"
            name="rpe"
            value={set.rpe}
            onChange={(evt) => {
              handler({
                value: evt.target.value,
                field: "rpe",
                exerciseId: exercise._id,
                setIdx: idx,
              });
            }}
          >
            <option value="">{`${intensityMeasure}`}</option>
            {map(range(4, 9.5, 0.5), (i) => (
              <option key={i} value={i + 1}>
                {convertToRiR(i + 1, !isRiR)}
              </option>
            ))}
            <option value="AMRAP">AMRAP</option>
          </select>
        );
      } else if (_param === LOAD && hasLoadDrop) {
        return (
          <>
            <input
              className="text-left!"
              value={set.loadDrop}
              onChange={(evt) => {
                if (String(evt.target.value).includes(",")) {
                  evt.target.value = String(evt.target.value).replace(",", ".");
                }
                handler({
                  value: evt.target.value,
                  field: "loadDrop",
                  exerciseId: exercise._id,
                  setIdx: idx,
                });
              }}
            />
            <span className="units-label">% mod.</span>
          </>
        );
      } else if (_param === LOAD) {
        return (
          <>
            <input
              className="text-input"
              id={`sets-${exercise._id}-${idx}`}
              defaultValue={set[param] > 0 ? fromMetric(set[param]) : ""}
              onChange={(evt) => {
                if (String(evt.target.value).includes(",")) {
                  evt.target.value = String(evt.target.value).replace(",", ".");
                }
                if (isNaN(evt.target.value)) return;
                let value = evt.target.value;
                if (units === "imperial" && value !== "") {
                  value = round(convert(value).from("lb").to("kg"), 2);
                }
                handler({
                  value,
                  field: param,
                  exerciseId: exercise._id,
                  setIdx: `${exercise._id}-${isTemporal ? "-new" : idx}`,
                });
              }}
            />
            <span className="units-label">
              {units === "imperial" ? "lb" : "kg"}
            </span>
          </>
        );
      } else {
        return (
          <input
            className="text-input"
            id={`sets-${exercise._id}-${idx}`}
            value={set[param]}
            onChange={(evt) =>
              handler({
                value: evt.target.value,
                field: param,
                exerciseId: exercise._id,
                setIdx: `${exercise._id}-${isTemporal ? "-new" : idx}`,
              })
            }
          />
        );
      }
    } else if ([RPE, RPEAMRAP, "rpe/amrap"].includes(param)) {
      // A logged set commonly carries rpe: 0, which convertToRiR passes
      // straight through — blank it rather than showing a bogus 0.
      return source.rpe ? convertToRiR(source.rpe, !isRiR) : "";
    } else if (_param === LOAD && source.loadDrop) {
      return getLoadDrop(source.loadDrop);
    } else if (_param === LOAD && source.load > 0) {
      return withWeight(fromMetric(source.load));
    } else if (_param === REPS) {
      return repsValue(source.reps, source.repsLower);
    } else {
      // mapParameter, not toLowerCase: "Free-Form" keys as `freeform`.
      const value = source[mapParameter(_param)] ?? source[param];
      // A prescribed velocity is a plain number, but a logged one is an object
      // ({ simple, detailed }) that React cannot render.
      return value && typeof value === "object"
        ? getVelocityValue(value)
        : value;
    }
  };

  // Finished view: swap prescribed values for the performed ones, one row per
  // performed round numbered 1,2,3… within its own set-group (target). A Sets
  // column here holds that number rather than the group's count, matching the
  // powerlifting table (see TableRow).
  if (hasActuals && !(editDayMode && editable)) {
    return (
      <>
        {expandSetOutcomes(set).map(({ index, entry }) => {
          const { outcome, status } = entry || {};
          const source = entry && status ? outcome : set;
          const setNumber = setNumberOffset + index + 1;

          return (
            <tr
              key={`${idx}-${index}`}
              className={
                status === "completed"
                  ? "ok"
                  : status === "missed"
                    ? "missed"
                    : "pending"
              }
            >
              {exercise?.parameters?.map((_param = "", idy) => (
                <td key={_param}>
                  {idy === 0 && (
                    <span className="wk-status-inline">
                      <StatusIcon status={status} />
                    </span>
                  )}
                  {_param === SETS
                    ? setNumber
                    : renderParameter(_param, source)}
                </td>
              ))}
              {/* Rendered empty on non-PR rows so the columns stay aligned. */}
              {hasPR ? (
                <td className="pr-badge-col">
                  {prByRound[`${idx}-${index}`] != null && (
                    <PRBadge reps={prByRound[`${idx}-${index}`]} />
                  )}
                </td>
              ) : null}
            </tr>
          );
        })}
      </>
    );
  }

  return (
    <tr key={idx}>
      {exercise &&
        exercise.parameters &&
        exercise.parameters.map((_param = "", idy) => {
          return <td key={idy}>{renderParameter(_param)}</td>;
        })}
      {exercise.sets.length && editDayMode && editable ? (
        <td className="actions">
          <button
            data-button-type="secondary"
            onClick={() => {
              addTempSet({ exerciseId: exercise._id, pos: idx });
            }}
          >
            <img
              src="/img/new-icons/add-gradient-icon.svg"
              height={24}
              width={24}
              alt="plus icon"
            />
          </button>
          <button
            data-button-type="secondary"
            onClick={() => {
              removeSet(exercise._id, idx);
            }}
          >
            <img
              src="/img/new-icons/red-trash.svg"
              height={18}
              width={18}
              alt="trash icon"
            />
          </button>
        </td>
      ) : null}
    </tr>
  );
};

export default TableForAssistant;
